/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {AugmentedSmarterTrailerSensorData, CircuitStatus} from '@ion/core/src/types/entities';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {isCircuitFaulty, validateUnknownCircuit} from '@ion/dls/src/components/smarterTrailer/helper';
import EventTime from '@ion/dls/src/components/time/EventTime';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import LightOutDetectionIcon from '@ion/dls/src/icons/LightOutDetectionIcon';
import {NotificationUrgentIcon} from '@ion/dls/src/icons/NotificationUrgentIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';

export interface LightOutSectionProps {
  trailerData: AugmentedSmarterTrailerSensorData;
  sensorUpdateLoading?: boolean;
}

export interface LightOutDetectionData {
  leftTurnLampCircuitStatus: CircuitStatus;
  rightTurnLampCircuitStatus: CircuitStatus;
  stopLampCircuitStatus: CircuitStatus;
  tailLampCircuitStatus: CircuitStatus;
  clearanceLampCircuitStatus: CircuitStatus;
}

type CircuitsTypes = {
  connectorId: string;
  connectorName: string;
  connectorStatus: CircuitStatus;
};

export const useStyles = makeStyles((theme: DLSTheme) => {
  const contentStyles = getAssetDetailsContentStyles();
  return {
    circuit : {
      padding : '0px 6px'
    },
    hoveredCircuit : {
      padding         : '0px 6px',
      backgroundColor : dlsColor('paleBlue2'),
      outline         : `1px solid ${dlsColor('caBlue')}`
    },
    lightOutDetection : {
      display        : 'flex',
      justifyContent : 'space-evenly',
      padding        : '4px 0px',
      cursor         : 'default'
    },
    iconWrapper : {
      marginTop : 12
    },
    connectorText : {
      ...dlsAlt('typography', 'cta2'),
      color         : dlsColor('grey2'),
      paddingTop    : 4,
      letterSpacing : '0.5px',
      lineHeight    : '16px'
    },
    connectorStatusText : {
      ...dlsAlt('typography', 'label2'),
      color         : dlsColor('grey3'),
      letterSpacing : '0px',
      textTransform : 'capitalize',
      paddingBottom : '4px',
      display       : 'flex'
    },
    eventTime : {
      padding : '0px 15px 15px'
    },
    // current theme only - drop the legacy inner/outer padding (the card
    // already supplies 16px side padding) and map the connector text to the
    // shared body text / meta typography ...
    currentEventTime : {
      padding : 0
    },
    currentConnectorText : {
      ...contentStyles.text
    },
    currentConnectorStatusText : {
      ...contentStyles.meta,
      textTransform : 'capitalize',
      display       : 'flex'
    }
  };
});

/**
 * @LightOutDetectionSection - Renders the Light Out Detection section
 *
 * The Light Out Detection section displays the status of the trailer's light out detection system.
 * It does this via the LightOutDetectionIcon component, which displays the status of each of the
 * trailer's light circuits.
 *
 * It also creates and displays a table of the status of each of the trailer's light circuits that
 * should highlight upon hover of the corresponding connector on the LightOutDetectionIcon component.
 *
 */
const LightOutDetectionSection = ({trailerData, sensorUpdateLoading}: LightOutSectionProps) => {
  const classes                                 = useStyles();
  const {isCurrent}                             = useThemeContext();
  const {LMS: {LA1: lightOutData}}              = trailerData || {};
  const {isPhone}                               = useMedia();
  const [hoveredConnector, setHoveredConnector] = useState(null);
  const {messageTime}                           = lightOutData || {};

  /**
   * @validateCircuitStatus - Validates the circuit status and returns the translated text or unknown
   */
  const validateCircuitStatus = useCallback((circuitStatus: string) => {
    return (circuitStatusMap?.[circuitStatus] || circuitStatusMap[CircuitStatus.UNKNOWN]).toUpperCase();
  }, []);

  const {
    leftTurnLampCircuitStatus: leftTurnHazard,
    rightTurnLampCircuitStatus: rightTurnHazard,
    stopLampCircuitStatus: stopLamps,
    tailLampCircuitStatus: tailRunningLights,
    clearanceLampCircuitStatus: clearanceSideMarkers
  } = lightOutData || {} as any;

  /**
   * @circuitsStatusMap - Map of circuit status to the translated text
   *
   * Because there are many different circuit statuses, we need to map them to the translated text
   * as we only want to display the translated text in the UI and not the actual status.
   *
   * @returns {object} - Map of circuit status to the translated text
   */
  const circuitStatusMap = useMemo(() => ({
    [CircuitStatus.GOOD]         : translate('common:smarterTrailer.lightOutDetection.status.good'),
    [CircuitStatus.RECONNECTED]  : translate('common:smarterTrailer.lightOutDetection.status.reconnected'),
    [CircuitStatus.DISCONNECTED] : translate('common:smarterTrailer.lightOutDetection.status.disconnected'),
    [CircuitStatus.LOW_CURRENT]  : translate('common:smarterTrailer.lightOutDetection.status.lowCurrent'),
    [CircuitStatus.HIGH_CURRENT] : translate('common:smarterTrailer.lightOutDetection.status.highCurrent'),
    [CircuitStatus.INTERMITTENT] : translate('common:smarterTrailer.lightOutDetection.status.intermittent'),
    [CircuitStatus.UNKNOWN]      : translate('common:smarterTrailer.lightOutDetection.status.unknown')
  }), []);

  // Ground and Auxiliary status are not provided by the API. Because of this,
  // we need to validate the status of the other circuits to determine the status of the ground and auxiliary circuits.
  const unknownCircuitStatus = useMemo(() => validateUnknownCircuit([
    clearanceSideMarkers,
    tailRunningLights,
    rightTurnHazard,
    leftTurnHazard,
    stopLamps
  ]), [
    clearanceSideMarkers,
    tailRunningLights,
    rightTurnHazard,
    leftTurnHazard,
    stopLamps
  ]);

  /**
   * @circuits - Array of circuits to be displayed in the table
   */
  const circuits = useMemo(() => [
    {
      circuitId     : 'ground',
      circuitName   : translate('common:smarterTrailer.lightOutDetection.circuits.ground'),
      circuitStatus : unknownCircuitStatus
    },
    {
      circuitId     : 'clearanceSideMarkers',
      circuitName   : translate('common:smarterTrailer.lightOutDetection.circuits.clearanceSideMarkers'),
      circuitStatus : clearanceSideMarkers
    },
    {
      circuitId     : 'leftTurnHazard',
      circuitName   : translate('common:smarterTrailer.lightOutDetection.circuits.leftTurnHazard'),
      circuitStatus : leftTurnHazard
    },
    {
      circuitId     : 'stopLamps',
      circuitName   : translate('common:smarterTrailer.lightOutDetection.circuits.stopLamps'),
      circuitStatus : stopLamps
    },
    {
      circuitId     : 'rightTurnHazard',
      circuitName   : translate('common:smarterTrailer.lightOutDetection.circuits.rightTurnHazard'),
      circuitStatus : rightTurnHazard
    },
    {
      circuitId     : 'tailRunningLights',
      circuitName   : translate('common:smarterTrailer.lightOutDetection.circuits.tailRunningLights'),
      circuitStatus : tailRunningLights
    },
    {
      circuitId     : 'auxiliary',
      circuitName   : translate('common:smarterTrailer.lightOutDetection.circuits.auxiliary'),
      circuitStatus : unknownCircuitStatus
    }
  ], [
    clearanceSideMarkers,
    leftTurnHazard,
    stopLamps,
    rightTurnHazard,
    tailRunningLights
  ] as Array<CircuitsTypes>);

  // Callback function to set the hovered connector from the LightOutDetectionIcon component
  const onCircuitHover = function(connectorId: string) {
    setHoveredConnector(connectorId);
  };

  const connectorStatusTextClass = themedClassName(
    classes.connectorStatusText,
    classes.currentConnectorStatusText,
    isCurrent
  );

  if (!sensorUpdateLoading) {
    return (
      <>
        <div className={classes.lightOutDetection}>
          <div className={classes.iconWrapper}>
            <LightOutDetectionIcon onConnectorHover={onCircuitHover}
              ground={unknownCircuitStatus}
              clearanceSideMarkers={clearanceSideMarkers}
              leftTurnHazard={leftTurnHazard}
              stopLamps={stopLamps}
              rightTurnHazard={rightTurnHazard}
              tailRunningLights={tailRunningLights}
              auxiliary={unknownCircuitStatus}
              isLargeVariant={isPhone} />
          </div>
          <div>
            {circuits.map((circuit, index) => {
              const {circuitId, circuitName, circuitStatus} = circuit || {};
              return (
                <div className={hoveredConnector === circuitId ? classes.hoveredCircuit : classes.circuit}
                  key={`${index + 1}-${circuitId}`}>
                  <div className={themedClassName(classes.connectorText, classes.currentConnectorText, isCurrent)}>
                    {index + 1} {circuitName}
                  </div>
                  <div className={connectorStatusTextClass}
                    style={{
                      color        : isCircuitFaulty(circuitStatus) && dlsColor('caRed'),
                      borderBottom : index === circuits.length - 1 ? 'none' : `1px solid ${dlsColor('grey5')}`
                    }}>
                    {validateCircuitStatus(circuitStatus)}
                    {isCircuitFaulty(circuitStatus) && <NotificationUrgentIcon color={dlsColor('caRed')} size={IconSize.small} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className={themedClassName(classes.eventTime, classes.currentEventTime, isCurrent)}>
          <EventTime eventTime={messageTime} />
        </div>
      </>
    );
  }
  return null;
};

export default LightOutDetectionSection;
